import type { EpoRuleType } from '@/types/investor';
import type { ReconFeeSource } from '@/utils/banker-recon/feePaymentLedger';
/**
 * TypeScript types for Banker Reconciliation
 * Matches database schema: banker_recons and banker_recon_line_items tables
 */

import type {
  ProfitabilityRouting,
  ReconLoProfitabilityComponent
} from '@/utils/banker-recon/loProfitability';
import type {
  FhaUfmipCaptureStatus,
  FhaUfmipFlowMode
} from '@/utils/banker-recon/fhaUfmip';

// ============================================================================
// STATUS TYPES
// ============================================================================

export type BankerReconStatus = 'draft' | 'in_review' | 'reconciled' | 'finalized' | 'pushed' | 'error';

export type LineItemCategory =
  | 'warehouse_cost'
  | 'investor_cost'
  | 'lender_cost'
  | 'loan_revenue'
  | 'investor_revenue'
  | 'lo_deduction';

export type LineItemSource = 'extracted' | 'encompass' | 'manual' | 'doc_order';

export type CompensationType = 'flat_fee' | 'percentage';

// ============================================================================
// OVERRIDE TYPES
// ============================================================================

/** Stored in JSONB and used in UI state — allows incomplete entries during editing */
export interface DraftOverrideEntry {
  id: string;
  recipientId: string | null;
  recipientName?: string | null;
  bps: number | null;
}

/** Normalized for calculation/finalization — fully resolved, never persisted directly */
export interface OverrideEntry {
  id: string;
  recipientId: string;
  recipientName?: string | null;
  bps: number;
}

/** Configured override intent, including an intentional 0 bps override reduction. */
export type ConfiguredOverrideEntry = OverrideEntry;

/** Filter draft entries to rows with a recipient and a numeric bps value, including 0 bps. */
export function getConfiguredOverrides(drafts: DraftOverrideEntry[]): ConfiguredOverrideEntry[] {
  return drafts.filter(
    (d): d is ConfiguredOverrideEntry =>
      !!d.recipientId && typeof d.bps === 'number' && Number.isFinite(d.bps) && d.bps >= 0
  );
}

/** Filter draft entries to only those with both recipientId and bps > 0 */
export function getActiveOverrides(drafts: DraftOverrideEntry[]): OverrideEntry[] {
  return drafts.filter(
    (d): d is OverrideEntry =>
      !!d.recipientId && typeof d.bps === 'number' && Number.isFinite(d.bps) && d.bps > 0
  );
}

// ============================================================================
// LINE ITEM TYPES
// ============================================================================

/**
 * Standard line types for each category
 */
export const LINE_TYPES = {
  warehouse_cost: [
    'shortage_funded',
    'warehouse_interest',
    'warehouse_funding_fee',
    'warehouse_recon_fee',
    'warehouse_du_fee',
    'collateral_dwell_fee'
  ],
  investor_cost: [
    'investor_admin_fee',
    'interest_rate_buydown',
    'interest_rate_buydown_auto'
  ],
  lender_cost: [
    'lender_credit'
  ],
  loan_revenue: [
    'processing_fee',
    'underwriting_fee',
    'interest_charged'
    // shortage_recouped removed — shortage modeled exclusively via shortage_funded (warehouse_cost)
    // with signed amounts. Negative = lender-funded recoup, positive = warehouse-charged cost.
  ],
  investor_revenue: [
    'srp_premium',
    'interest_credit'
  ]
} as const;

export type WarehouseCostType = typeof LINE_TYPES.warehouse_cost[number];
export type InvestorCostType = typeof LINE_TYPES.investor_cost[number];
export type LenderCostType = typeof LINE_TYPES.lender_cost[number];
export type LoanRevenueType = typeof LINE_TYPES.loan_revenue[number];
export type InvestorRevenueType = typeof LINE_TYPES.investor_revenue[number];

export type LineType =
  | WarehouseCostType
  | InvestorCostType
  | LenderCostType
  | LoanRevenueType
  | InvestorRevenueType;

// ============================================================================
// DATABASE TYPES
// ============================================================================

/**
 * Line item for costs/revenue (matches banker_recon_line_items table)
 */
export interface BankerReconLineItem {
  fee_source?: ReconFeeSource | null;
  id: string;
  banker_recon_id: string;
  category: LineItemCategory;
  line_type: string;
  description: string;
  amount: number; // Signed: positive = standard cost/revenue; negative in cost categories = credit/recoup
  source: LineItemSource;
  confidence?: number; // 0.00-1.00 for AI extractions
  original_value?: number; // Value before manual override
  display_order: number;
  reimbursable?: boolean; // Whether fee is reimbursed to LO
  nets_to_warehouse_advance?: boolean | null; // Credit/cure timing for warehouse cash-flow only
  warehouse_retained_amount?: number | null; // Retained-at-warehouse amount excluded from P&L (basis = amount + this)
  created_at: string;
  updated_at: string;
}

/**
 * Main banker reconciliation record (matches banker_recons table)
 */
export interface BankerRecon {
  servicing_template_id?: string | null;
  servicing_template_selected_by?: string | null;
  servicing_template_selected_at?: string | null;
  servicing_template_selection_source?: 'manual' | 'suggested_pa' | 'suggested_state' | 'auto_single' | null;
  servicing_template_evidence?: { matchedCode?: string; snippet?: string; state?: string } | null;

  id: string;
  organization_id: string;

  // Loan link
  encompass_loan_id: string;
  loan_number: string;
  loan_amount: number;

  // Status
  status: BankerReconStatus;

  // Key dates
  closing_date?: string;
  funds_to_escrow_date?: string;
  funds_received_date?: string;
  investor_purchase_date?: string;
  first_payment_to_investor?: string;
  early_payoff_date?: string | null;
  early_payoff_eligible_at?: string | null;
  early_payoff_calculation_status?:
    | 'calculated'
    | 'missing_investor'
    | 'missing_rule'
    | 'missing_purchase_date'
    | 'missing_first_payment_date'
    | 'invalid_rule'
    | 'unsupported_rule'
    | null;
  early_payoff_calculation_message?: string | null;
  early_payoff_rule_version_id?: string | null;
  early_payoff_rule_type?: EpoRuleType | null;
  early_payoff_rule_value?: number | null;
  early_payoff_rule_channel?: 'wholesale' | 'correspondent' | null;
  early_payoff_calculated_at?: string | null;

  // Warehouse details
  warehouse_days: number;
  warehouse_daily_rate?: number;

  // Investor pricing (expected vs actual)
  investor_id?: string;
  expected_rate_lock_price?: number; // e.g., 102.771
  company_margin_percent?: number; // e.g., 0.572 (stored as %, not decimal)
  expected_total_price?: number; // rate_lock + margin
  actual_purchase_price?: number; // From Purchase Advice
  pricing_variance?: number; // Actual - Expected (positive = pickup)
  purchase_adjustments?: Array<{
    id: string;
    description: string;
    basisPoints: number;
  }>; // Price adjustments: Mandatory Trade, Extensions, Suspense Fee
  pricing_source_type?: 'encompass_current' | 'delivery_lock';
  pricing_source_lock_version_id?: string | null;
  pricing_source_lock_snapshot_id?: string | null;
  pricing_source_selection_id?: string | null;
  pricing_source_snapshot?: Record<string, unknown> | null;

  // Compensation policy (from recruits table)
  lo_comp_percent?: number; // e.g., 1.5 (stored as %, not decimal)

  // Multiple overrides (JSONB array — authoritative source)
  overrides?: DraftOverrideEntry[];

  /** @deprecated Use overrides[] instead */
  override_bps?: number | null;
  /** @deprecated Use overrides[] instead */
  override_recipient_id?: string | null;
  /** @deprecated Use overrides[] instead */
  override_recipient?: { first_name: string; last_name: string } | null;

  // Lender credit
  lender_credit_given: number;

  // Computed totals (total_costs/total_revenue maintained by trigger; net_profit by application)
  total_costs?: number;
  total_revenue?: number;
  net_profit?: number;

  // LO compensation
  lo_user_id?: string;
  /** Payee name embedded via lo_user_id by listBankerRecons; null when users RLS hides the row. */
  lo?: { first_name: string | null; last_name: string | null } | null;
  compensation_type?: CompensationType;
  compensation_value?: number;
  lo_payout?: number;
  /** Separate cash installment; ordinary LO payout and profitability are unchanged. */
  volume_bonus_amount?: number;
  volume_bonus_first_finalized_at?: string | null;
  company_revenue?: number;

  // Company revenue breakdown
  processing_fee: number;
  underwriting_fee: number;
  interest_income: number; // Prepaid interest charged at closing (snapshot)
  gos_amount: number; // Gain on Sale

  // Reimbursables
  total_reimbursable?: number; // Sum of fees marked as reimbursable to LO

  // Wire reconciliation
  wire_from_investor?: number; // Gross wire amount from investor to warehouse
  wire_from_warehouse?: number; // Gross wire amount from warehouse to us

  // Warehouse lender credits
  flexclose_credit?: number; // FlexClose credit from warehouse lender (positive, reduces effective WH costs)
  // Non-QM haircut percentage (e.g., 2% for Non-QM loans — reduces warehouse advance)
  warehouse_haircut_percent?: number;

  // Servicing interest income (from interim payments — separate from interest_income which is prepaid)
  servicing_interest_income?: number;
  // Principal collected during interim servicing (informational — not P&L revenue)
  servicing_principal_collected?: number;

  // FHA UFMIP frozen accounting snapshot
  fha_ufmip_flow_mode?: FhaUfmipFlowMode;
  fha_ufmip_premium_amount?: number | null;
  fha_ufmip_financed_amount?: number | null;
  fha_ufmip_remitted_date?: string | null;
  fha_ufmip_remitted_by?: string | null;
  fha_ufmip_remitted_amount?: number | null;
  fha_ufmip_verification_code?: string | null;
  fha_ufmip_capture_status?: FhaUfmipCaptureStatus;
  fha_ufmip_source_signature?: string | null;
  fha_ufmip_capture_details?: Record<string, unknown> | null;
  fha_ufmip_snapshot_at?: string | null;
  fha_ufmip_company_variance?: number;

  // LO Profitability (routing-aware, signed, report-only — see utils/banker-recon/loProfitability.ts)
  profitability_routing?: ProfitabilityRouting;
  lo_profitability_gross?: number | null;
  lo_profitability_net?: number | null;
  lo_profitability_components?: ReconLoProfitabilityComponent[] | null;

  // Audit trail
  created_by?: string;
  created_at: string;
  updated_by?: string;
  updated_at: string;
  finalized_by?: string;
  finalized_at?: string;
  pushed_to_encompass_at?: string;

  // Loan snapshot
  loan_snapshot?: Record<string, unknown>;
  snapshot_at?: string;

  // Payment snapshot (temporary, preserved during unfinalize/re-finalize)
  payment_snapshot?: Array<{
    recipient_id: string;
    payout_type: string;
    payout_sequence: number;
    status: string;
    payment_amount: number;
    payment_check_number: string | null;
    payment_date: string | null;
  }> | null;
}

// ============================================================================
// INPUT/CREATE TYPES
// ============================================================================

/**
 * Input for creating a new banker reconciliation
 */
export interface CreateBankerReconInput {
  organization_id: string;
  encompass_loan_id: string;
  loan_number: string;
  loan_amount: number;
  lo_user_id?: string;
  investor_id?: string;
  closing_date?: string;
  funds_to_escrow_date?: string;
  funds_received_date?: string;
  investor_purchase_date?: string;
  loan_snapshot?: Record<string, unknown>;
  pricing_source_type?: 'encompass_current' | 'delivery_lock';
  pricing_source_lock_version_id?: string | null;
  pricing_source_lock_snapshot_id?: string | null;
  pricing_source_selection_id?: string | null;
  pricing_source_snapshot?: Record<string, unknown> | null;
}

/**
 * Input for creating a line item
 */
export interface CreateLineItemInput {
  fee_source?: ReconFeeSource | null;
  banker_recon_id: string;
  category: LineItemCategory;
  line_type: string;
  description: string;
  amount: number;
  source?: LineItemSource;
  confidence?: number;
  display_order?: number;
  reimbursable?: boolean;
  nets_to_warehouse_advance?: boolean | null;
  warehouse_retained_amount?: number | null;
}

/**
 * Input for updating a banker reconciliation
 */
export interface UpdateBankerReconInput {
  volume_bonus_amount?: number;
  servicing_template_id?: string | null;
  servicing_template_selected_by?: string | null;
  servicing_template_selected_at?: string | null;
  servicing_template_selection_source?: 'manual' | 'suggested_pa' | 'suggested_state' | 'auto_single' | null;
  servicing_template_evidence?: { matchedCode?: string; snippet?: string; state?: string } | null;

  id: string;
  warehouse_days?: number;
  warehouse_daily_rate?: number;
  expected_rate_lock_price?: number;
  company_margin_percent?: number;
  expected_total_price?: number;
  actual_purchase_price?: number;
  pricing_variance?: number;
  lo_comp_percent?: number;
  // Multiple overrides
  overrides?: DraftOverrideEntry[];
  /** @deprecated Kept for legacy mirror */
  override_bps?: number | null;
  /** @deprecated Kept for legacy mirror */
  override_recipient_id?: string | null;
  lender_credit_given?: number;
  compensation_type?: CompensationType;
  compensation_value?: number;
  lo_payout?: number;
  company_revenue?: number;
  processing_fee?: number;
  underwriting_fee?: number;
  gos_amount?: number;
  interest_income?: number;
  net_profit?: number;
  total_reimbursable?: number;
  status?: BankerReconStatus;
  finalized_by?: string;
  // Investor purchase date
  investor_purchase_date?: string;
  // First payment to investor date
  first_payment_to_investor?: string;
  // Purchase price adjustments
  purchase_adjustments?: Array<{
    id: string;
    description: string;
    basisPoints: number;
  }>;
  pricing_source_type?: 'encompass_current' | 'delivery_lock';
  pricing_source_lock_version_id?: string | null;
  pricing_source_lock_snapshot_id?: string | null;
  pricing_source_selection_id?: string | null;
  pricing_source_snapshot?: Record<string, unknown> | null;
  // Wire reconciliation
  wire_from_investor?: number;
  wire_from_warehouse?: number;
  // Warehouse lender credits
  flexclose_credit?: number;
  // Non-QM haircut percentage
  warehouse_haircut_percent?: number;
  // Servicing interest income (from interim payments)
  servicing_interest_income?: number;
  // Principal collected during interim servicing (informational — informational)
  servicing_principal_collected?: number;
  // FHA UFMIP frozen accounting snapshot
  fha_ufmip_flow_mode?: FhaUfmipFlowMode;
  fha_ufmip_premium_amount?: number | null;
  fha_ufmip_financed_amount?: number | null;
  fha_ufmip_remitted_date?: string | null;
  fha_ufmip_remitted_by?: string | null;
  fha_ufmip_remitted_amount?: number | null;
  fha_ufmip_verification_code?: string | null;
  fha_ufmip_capture_status?: FhaUfmipCaptureStatus;
  fha_ufmip_source_signature?: string | null;
  fha_ufmip_capture_details?: Record<string, unknown> | null;
  fha_ufmip_snapshot_at?: string | null;
  fha_ufmip_company_variance?: number;
  // LO Profitability (routing-aware, signed, report-only — see utils/banker-recon/loProfitability.ts)
  profitability_routing?: ProfitabilityRouting;
  lo_profitability_gross?: number;
  lo_profitability_net?: number;
  lo_profitability_components?: ReconLoProfitabilityComponent[];
}

// ============================================================================
// PAYOUT TYPES (for finalization)
// ============================================================================

/**
 * Input for creating a payout from banker recon
 */
export interface CreateBankerReconPayoutInput {
  organization_id: string;
  recipient_id: string;
  source_officer_id?: string;
  loan_number: string;
  borrower_name?: string;
  loan_amount: number;
  fund_date?: string;
  gross_amount: number;
  net_amount: number;
  e_and_o_deduction?: number;
  payout_type: 'banked';
  payout_sequence: number;
  source_type: 'banker-recon';
  banker_recon_id: string;
  status: 'pending' | 'approved';
}

// ============================================================================
// UI STATE TYPES (for component state management)
// ============================================================================

export interface KeyDatesState {
  closingDate: string;
  fundsToEscrow: string;
  fundsReceived: string;
  warehouseDays: number;
  dailyInterestRate: number;
}

export interface PricingState {
  rateLockPrice: number;
  profitMargin: number;
  actualPricePercent: number;
}

export interface CompensationState {
  type: CompensationType;
  value: number;
  notes: string;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Calculate LO payout based on compensation policy from recruits table
 */
export function calculateLoPayout(
  loanAmount: number,
  loCompPercent: number // Stored as percent (1.5 = 1.5%)
): number {
  return loanAmount * loCompPercent / 100;
}

/**
 * Calculate override payout based on BPS from recruits table
 */
export function calculateOverridePayout(
  loanAmount: number,
  overrideBps: number // Stored as basis points (25 = 0.25%)
): number {
  return loanAmount * overrideBps / 10000;
}

/**
 * Determine if a category represents a cost (negative) or revenue (positive)
 */
export function isCostCategory(category: LineItemCategory): boolean {
  return category.endsWith('_cost');
}

/**
 * Calculate expected total price from rate lock + margin
 */
export function calculateExpectedPrice(rateLockPrice: number, marginPercent: number): number {
  return rateLockPrice + marginPercent;
}

/**
 * Calculate pricing variance (positive = pickup, negative = miss)
 */
export function calculatePricingVariance(actualPrice: number, expectedPrice: number): number {
  return actualPrice - expectedPrice;
}
